// ============================================================================
// packageCreditsStorage — CRUD de client_package_credits (Supabase REST)
// ============================================================================

import { SUPABASE_URL, restHeaders } from './secureRest.js';

const num = (v) => Number(v) || 0;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = (id) => typeof id === 'string' && UUID_RE.test(id.trim());
const asUuidOrNull = (v) => (isUUID(v) ? v.trim() : null);

const toApp = (r) => !r ? null : ({
  id: r.id,
  clientId: r.client_id || '',
  packageId: r.package_id || '',
  packageName: r.package_name || '',
  serviceId: r.service_id || '',
  serviceName: r.service_name || '',
  qtyTotal: num(r.qty_total),
  qtyRemaining: num(r.qty_remaining),
  valuePerUse: num(r.value_per_use),
  valueFull: num(r.value_full),
  commissionMode: r.commission_mode || 'with_discount',
  sourceComandaId: r.source_comanda_id || '',
  sourceUnitId: r.source_unit_id || '',
  grantedAt: r.granted_at || '',
  expiresAt: r.expires_at || null,
  active: r.active !== false,
  notes: r.notes || '',
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

const toDb = (c) => {
  const r = {};
  if (c.clientId !== undefined)        r.client_id          = asUuidOrNull(c.clientId);
  if (c.packageId !== undefined)       r.package_id         = c.packageId || '';
  if (c.packageName !== undefined)     r.package_name       = c.packageName || null;
  if (c.serviceId !== undefined)       r.service_id         = c.serviceId || '';
  if (c.serviceName !== undefined)     r.service_name       = c.serviceName || null;
  if (c.qtyTotal !== undefined)        r.qty_total          = num(c.qtyTotal);
  if (c.qtyRemaining !== undefined)    r.qty_remaining      = num(c.qtyRemaining);
  if (c.valuePerUse !== undefined)     r.value_per_use      = num(c.valuePerUse);
  if (c.valueFull !== undefined)       r.value_full         = num(c.valueFull);
  if (c.commissionMode !== undefined)  r.commission_mode    = c.commissionMode || 'with_discount';
  if (c.sourceComandaId !== undefined) r.source_comanda_id  = asUuidOrNull(c.sourceComandaId);
  if (c.sourceUnitId !== undefined)    r.source_unit_id     = c.sourceUnitId || null;
  if (c.grantedAt !== undefined)       r.granted_at         = c.grantedAt || null;
  if (c.expiresAt !== undefined)       r.expires_at         = c.expiresAt || null;
  if (c.active !== undefined)          r.active             = !!c.active;
  if (c.notes !== undefined)           r.notes              = c.notes || null;
  return r;
};

async function patchOrPost(table, id, row) {
  if (isUUID(id)) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(await r.text());
    return (await r.json())[0];
  }
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json())[0];
}

export async function listPackageCredits({ clientId, activeOnly = false, limit = 5000 } = {}) {
  const qs = ['select=*', `order=granted_at.desc`, `limit=${limit}`];
  if (clientId && isUUID(clientId)) qs.push(`client_id=eq.${clientId}`);
  if (activeOnly) qs.push('active=eq.true');
  const r = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?${qs.join('&')}`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(toApp) : [];
}

export async function upsertPackageCredit(c) {
  const row = toDb(c);
  return toApp(await patchOrPost('client_package_credits', c.id, row));
}

// Cria N créditos de uma só vez (1 por item do pacote). Usado quando uma
// comanda com pacote é finalizada.
export async function createCreditsBatch(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const body = rows.map(toDb);
  const r = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json()).map(toApp);
}

// Decrementa qty_remaining de um crédito. Se chegar a zero, marca active=false.
export async function consumePackageCredit(id, qty = 1) {
  if (!isUUID(id)) throw new Error('id de crédito inválido');
  // Lê estado atual (single)
  const rd = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?id=eq.${id}&select=qty_remaining`, { headers: restHeaders() });
  if (!rd.ok) throw new Error(await rd.text());
  const cur = (await rd.json())[0];
  if (!cur) throw new Error('crédito não encontrado');
  const remaining = Math.max(0, num(cur.qty_remaining) - num(qty));
  const body = { qty_remaining: remaining, active: remaining > 0, updated_at: new Date().toISOString() };
  const r = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?id=eq.${id}`, {
    method: 'PATCH',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return toApp((await r.json())[0]);
}

// REVERSO de consumePackageCredit. Incrementa qty_remaining (capped no
// qty_total) e reativa se necessário. Usado quando comanda finalizada é
// REABERTA ou CANCELADA — pra não consumir o crédito 2× no próximo finalize.
export async function refundPackageCredit(id, qty = 1) {
  if (!isUUID(id)) throw new Error('id de crédito inválido');
  const rd = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?id=eq.${id}&select=qty_remaining,qty_total`, { headers: restHeaders() });
  if (!rd.ok) throw new Error(await rd.text());
  const cur = (await rd.json())[0];
  if (!cur) throw new Error('crédito não encontrado');
  const total = num(cur.qty_total);
  const remaining = Math.min(total, num(cur.qty_remaining) + num(qty));
  const body = { qty_remaining: remaining, active: true, updated_at: new Date().toISOString() };
  const r = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?id=eq.${id}`, {
    method: 'PATCH',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return toApp((await r.json())[0]);
}

// REMOVE (deleta) TODOS os créditos criados por uma comanda. Usado quando a
// comanda que vendeu o pacote é cancelada/reaberta — o pacote vendido "some".
//
// IMPORTANTE: deletamos (não apenas active=false) de propósito. Se apenas
// desativássemos, esses créditos fantasma continuariam:
//   (a) sendo contados no display do cliente (usadas/restantes inflado), e
//   (b) sendo devolvidos como "dupes" pela idempotência do grant ao refinalizar,
//       fazendo o refinalizar reusar créditos com qtyRemaining errado.
// Deletando por source_comanda_id removemos TODAS as linhas dessa comanda
// (ativas E fantasmas de ciclos anteriores), então ao refinalizar a
// idempotência não acha nada e cria créditos frescos e corretos. Isso também
// auto-cura comandas que já ficaram com saldo inflado: basta reabrir+refinalizar.
export async function deactivateCreditsBySource(comandaId) {
  if (!isUUID(comandaId)) return [];
  const r = await fetch(`${SUPABASE_URL}/rest/v1/client_package_credits?source_comanda_id=eq.${comandaId}`, {
    method: 'DELETE',
    headers: restHeaders({ Prefer: 'return=representation' }),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json()).map(toApp);
}

// Helper: dado um pacote (do localStorage) + comanda finalizada, monta a lista
// de credit rows pra inserir.
// pkg shape: { id, name, validityDays, items: [{serviceId, serviceName, value, quantity}],
//              priceWithDiscount, priceWithoutDiscount, commissionMode }
export function buildCreditsFromPackageSale({ pkg, clientId, comandaId, unitId, soldAt }) {
  if (!pkg || !Array.isArray(pkg.items) || pkg.items.length === 0) return [];
  if (!isUUID(clientId)) return [];

  const totalQty = pkg.items.reduce((s, it) => s + (num(it.quantity)), 0) || 1;
  const liquido  = num(pkg.priceWithDiscount ?? pkg.price);
  const bruto    = num(pkg.priceWithoutDiscount) || liquido;
  // Comissão default = with_discount → valor por uso usado pra comissão é
  // o liquido rateado. without_discount = item.value cheio.
  const valuePerUseLiquido = totalQty > 0 ? +(liquido / totalQty).toFixed(2) : 0;

  const granted = (soldAt ? new Date(soldAt) : new Date());
  const grantedISO = granted.toISOString().slice(0, 10);
  const validity = num(pkg.validityDays);
  let expiresAt = null;
  if (validity > 0) {
    const exp = new Date(granted);
    exp.setDate(exp.getDate() + validity);
    expiresAt = exp.toISOString().slice(0, 10);
  }

  return pkg.items
    .filter(it => it.serviceId && num(it.quantity) > 0)
    .map(it => ({
      clientId,
      packageId: pkg.id,
      packageName: pkg.name || '',
      serviceId: it.serviceId,
      serviceName: it.serviceName || '',
      qtyTotal: num(it.quantity),
      qtyRemaining: num(it.quantity),
      // No commissionMode = with_discount usamos o rateio do líquido.
      // No without_discount cada uso pesa o valor cheio do próprio serviço.
      valuePerUse: pkg.commissionMode === 'without_discount' ? num(it.value) : valuePerUseLiquido,
      valueFull: num(it.value),
      commissionMode: pkg.commissionMode || 'with_discount',
      sourceComandaId: comandaId,
      sourceUnitId: unitId || '',
      grantedAt: grantedISO,
      expiresAt,
      active: true,
    }));
}
